import React, { useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import useErrors from "../../../libs/useErrors";

import { AtgResource, searchAtgResource } from "../../../services/atgServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import CommonInput from "../../Input/CommonInput";
import HorizontalRule from "../../Rule/Horizontal";
import PrimaryButton from "../../Button/Primary";

import ZoomSearchIcon from "../../../svgs/icons/ZoomSearchIcon";
import { NoDataIcon } from "../../../pages/Center";

type Props = {
  atgBuildingId: string;
  onChange?: (resource: AtgResource[]) => void;
  onClose?: () => void;
  prevSelectedResource?: AtgResource[];
};

export default function FindResource({
  atgBuildingId,
  onChange,
  onClose,
  prevSelectedResource,
}: Props) {
  const resourceRef = useRef<HTMLInputElement>(null);

  const [errors, { removeError, setError }] = useErrors();

  const [resourceName, setResourceName] = useState("");
  const [resourceList, setResourceList] = useState<AtgResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<AtgResource[]>(
    prevSelectedResource ?? []
  );
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const searchResource = () => {
    if (resourceName) {
      setIsLoading(true);
      searchAtgResource({ atgBuildingId, resourceName })
        .then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setResourceList(res.data);
            setIsSearch(true);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      setError("resource", "자재명은 필수값입니다.");
      resourceRef.current?.focus();
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {loadingComponent}
      <div className="px-5">
        <CommonInput
          placeholder="자재명을 입력해주세요"
          className="p-4 text-base w-full bg-brand-neutral-05"
          postfix={{
            content: (
              <div
                onClick={() => {
                  searchResource();
                }}
              >
                <ZoomSearchIcon />
              </div>
            ),
            className: "px-3",
          }}
          value={resourceName}
          error={errors["resource"]}
          onChange={(value: string) => {
            setResourceName(value);
            removeError("resource");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              searchResource();
            }
          }}
          ref={resourceRef}
        />
      </div>

      {isSearch &&
        (resourceList && resourceList.length > 0 ? (
          <div className="px-5">
            <HorizontalRule className="bg-black" margin="0" />
            <div className="px-2.5 py-[5px] flex items-center text-xs text-brand-neutral-60">
              <div className="flex flex-col flex-1" style={{ gap: -8 }}>
                <div>자재명/규격</div>
                <div>센터/창고</div>
              </div>
              <div className="w-[40px]">재고</div>
              <div className="w-[72px]">투입 수량</div>
            </div>
            <HorizontalRule className="bg-black" margin="0" />
            <div className="max-h-[50vh] overflow-scroll">
              {resourceList.map((resource, index) => {
                const isSelected = selectedResource.find(
                  (r) => r.materialCode === resource.materialCode
                );
                return (
                  <>
                    {index > 0 && <HorizontalRule margin="0" />}
                    <div className=" p-2.5 gap-2.5 flex flex-col">
                      <div className="flex items-center">
                        <div
                          className="flex flex-col flex-1 pr-4 gap-1 w-full"
                          // style={{ width: "calc(100% - 40px - 72px)" }}
                        >
                          <div>{resource.materialName}</div>
                          <div className="text-xs text-brand-neutral-60 break-all">
                            {resource.standardName}
                          </div>
                        </div>
                        <div className="min-w-[40px] text-sm">
                          {resource.inventory ?? "0"}
                        </div>
                        <div className="min-w-[72px] text-xs flex items-center gap-1.5">
                          <div
                            className={clsx([
                              {
                                "opacity-50":
                                  Number(resource.inventory) <= 0 ||
                                  Number(isSelected?.useQuantity) <= 0 ||
                                  isSelected?.useQuantity === undefined,
                              },
                            ])}
                            onClick={() => {
                              if (isSelected) {
                                if (Number(isSelected.useQuantity) <= 1) {
                                  setSelectedResource(
                                    selectedResource.filter(
                                      (r) =>
                                        r.materialCode !== resource.materialCode
                                    )
                                  );
                                } else {
                                  setSelectedResource(
                                    selectedResource.map((r) => {
                                      if (
                                        r.materialCode === resource.materialCode
                                      ) {
                                        return {
                                          ...r,
                                          useQuantity: (
                                            Number(r.useQuantity) - 1
                                          ).toString(),
                                        };
                                      }
                                      return r;
                                    })
                                  );
                                }
                              }
                            }}
                          >
                            <SearchResourceMinusIcon />
                          </div>
                          {isSelected ? isSelected.useQuantity : 0}
                          <div
                            className={clsx([
                              {
                                "opacity-50":
                                  Number(resource.inventory) <= 0 ||
                                  isSelected?.useQuantity ===
                                    resource.inventory,
                              },
                            ])}
                            onClick={() => {
                              if (isSelected) {
                                if (
                                  isSelected.useQuantity === resource.inventory
                                )
                                  return;
                                setSelectedResource(
                                  selectedResource.map((r) => {
                                    if (
                                      r.materialCode === resource.materialCode
                                    ) {
                                      return {
                                        ...r,
                                        useQuantity: (
                                          Number(r.useQuantity) + 1
                                        ).toString(),
                                      };
                                    }
                                    return r;
                                  })
                                );
                              } else {
                                if (
                                  resource.inventory === "0" ||
                                  resource.inventory === undefined ||
                                  resource.inventory === null
                                )
                                  return;
                                setSelectedResource([
                                  ...selectedResource,
                                  { ...resource, useQuantity: "1" },
                                ]);
                              }
                            }}
                          >
                            <SearchResourcePlusIcon />
                          </div>
                        </div>
                      </div>
                      <div className="p-[5px] bg-brand-neutral-05 text-xs text-brand-neutral-60">
                        <div>{resource.centerName}</div>
                        <div>{resource.storageName}</div>
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
            <NoDataIcon />
            조회 결과가 없어요
          </div>
        ))}

      <PrimaryButton
        className="text-white h-[50px] w-full flex justify-center items-center text-2xl font-bold"
        onClick={() => {
          if (!selectedResource || selectedResource.length === 0) {
            open("선택된 자재가 없습니다.");
            return;
          }

          onChange?.(selectedResource);
          onClose?.();
        }}
      >
        추가
      </PrimaryButton>
      {simplePopupComponent}
    </div>
  );
}

export const SearchResourceMinusIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="24" height="24" rx="2.96903" fill="#F2F2F2" />
    <path
      d="M15.5355 12.5001H8.46447"
      stroke="#868687"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <rect
      x="0.320976"
      y="0.820976"
      width="23.358"
      height="23.358"
      rx="2.64805"
      stroke="black"
      strokeOpacity="0.2"
      strokeWidth="0.641952"
    />
  </svg>
);

export const SearchResourcePlusIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.5" width="24" height="24" rx="2.96903" fill="#F2F2F2" />
    <path
      d="M15.5355 12.4999H8.46447"
      stroke="#868687"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M12 8.96436V16.0354"
      stroke="#868687"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <rect
      x="0.320976"
      y="0.820976"
      width="23.358"
      height="23.358"
      rx="2.64805"
      stroke="black"
      strokeOpacity="0.2"
      strokeWidth="0.641952"
    />
  </svg>
);
